/**
 * Entity System Types
 * 
 * Defines the component-based entity system for game objects.
 * Entities are composed of components that provide functionality.
 */

import { Vector3 } from "@babylonjs/core/Maths/math.vector";
import { Quaternion } from "@babylonjs/core/Maths/math.vector";
import { AbstractMesh } from "@babylonjs/core/Meshes/abstractMesh";
import { TransformNode } from "@babylonjs/core/Meshes/transformNode";
import { PhysicsAggregate } from "@babylonjs/core/Physics/v2/physicsAggregate";
import { AnimationGroup } from "@babylonjs/core/Animations/animationGroup";
import { Skeleton } from "@babylonjs/core/Bones/skeleton";
import { BoundingInfo } from "@babylonjs/core/Culling/boundingInfo";

/**
 * Entity state
 */
export enum EntityState {
    INACTIVE = "inactive",
    ACTIVE = "active",
    PAUSED = "paused",
    DESTROYED = "destroyed",
}

/**
 * Component types available
 */
export enum ComponentType {
    TRANSFORM = "transform",
    RENDER = "render",
    PHYSICS = "physics",
    ANIMATION = "animation",
    COLLIDER = "collider",
    INTERACTION = "interaction",
    AI = "ai",
    MOVEMENT = "movement",
}

/**
 * Base component interface
 */
export interface IComponent {
    type: ComponentType;
    enabled: boolean;
    initialize?(): void | Promise<void>;
    update?(deltaTime: number): void;
    dispose?(): void;
}

/**
 * Transform component data
 */
export interface TransformData {
    position: Vector3;
    rotation: Quaternion;
    scale: Vector3;
    node?: TransformNode;
}

/**
 * Render component data
 */
export interface RenderComponentData {
    meshes: AbstractMesh[];
    visible: boolean;
    castShadows: boolean;
    receiveShadows: boolean;
    layer?: number;
}

/**
 * Physics component data
 */
export interface PhysicsComponentData {
    aggregate: PhysicsAggregate | null;
    mass: number;
    isKinematic: boolean;
    isStatic: boolean;
    linearVelocity?: Vector3;
    angularVelocity?: Vector3;
}

/**
 * Animation component data
 */
export interface AnimationComponentData {
    skeleton: Skeleton | null;
    animationGroups: AnimationGroup[];
    currentAnimation: string | null;
    isPlaying: boolean;
    speed: number;
    loop: boolean;
}

/**
 * Collider shapes
 */
export enum ColliderShape {
    BOX = "box",
    SPHERE = "sphere",
    CAPSULE = "capsule",
    CYLINDER = "cylinder",
    MESH = "mesh",
    CONVEX = "convex",
}

/**
 * Collider component data
 */
export interface ColliderComponentData {
    shape: ColliderShape;
    bounds: BoundingInfo | null;
    isTrigger: boolean;
    collisionGroup: number;
    collisionMask: number;
    /** Custom dimensions override */
    customDimensions?: {
        width: number;
        height: number;
        depth: number;
    };
    /** Offset from entity center */
    offset?: Vector3;
}

/**
 * Interaction types
 */
export enum InteractionType {
    CLICK = "click",
    HOVER = "hover",
    PROXIMITY = "proximity",
    TRIGGER = "trigger",
}

/**
 * Interaction component data
 */
export interface InteractionComponentData {
    interactionTypes: InteractionType[];
    interactionRadius: number;
    isInteractable: boolean;
    highlightOnHover: boolean;
    interactionPrompt?: string;
}

/**
 * Movement modes
 */
export enum MovementMode {
    WALK = "walk",
    RUN = "run",
    FLYING = "flying",
    SWIMMING = "swimming",
}

/**
 * Movement component data
 */
export interface MovementComponentData {
    mode: MovementMode;
    walkSpeed: number;
    runSpeed: number;
    jumpForce: number;
    turnSpeed: number;
    isGrounded: boolean;
    canJump: boolean;
    maxSlopeAngle: number;
}

/**
 * Entity configuration for spawning
 */
export interface EntityConfig {
    id?: string;
    name: string;
    modelId?: string;
    position?: Vector3;
    rotation?: Quaternion;
    scale?: Vector3;
    physics?: {
        enabled: boolean;
        mass?: number;
        shape?: ColliderShape;
        friction?: number;
        restitution?: number;
    };
    animation?: {
        defaultAnimation?: string;
        autoPlay?: boolean;
        loop?: boolean;
    };
    interaction?: {
        enabled: boolean;
        radius?: number;
        prompt?: string;
    };
    tags?: string[];
    metadata?: Record<string, unknown>;
}

/**
 * Entity event types
 */
export type EntityEventType = 
    | "spawn"
    | "destroy"
    | "enable"
    | "disable"
    | "collision_enter"
    | "collision_exit"
    | "trigger_enter"
    | "trigger_exit"
    | "interaction"
    | "animation_end";

/**
 * Entity event data
 */
export interface EntityEvent {
    type: EntityEventType;
    entity: IEntity;
    data?: unknown;
    timestamp: number;
}

/**
 * Entity event handler
 */
export type EntityEventHandler = (event: EntityEvent) => void;

/**
 * Entity interface
 */
export interface IEntity {
    readonly id: string;
    readonly name: string;
    state: EntityState;
    tags: Set<string>;
    
    // Transform shortcuts
    position: Vector3;
    rotation: Quaternion;
    scale: Vector3;
    forward: Vector3;
    
    // Lifecycle
    initialize(): Promise<void>;
    update(deltaTime: number): void;
    dispose(): void;
    
    // Component access
    getComponent<T extends IComponent>(type: ComponentType): T | null;
    hasComponent(type: ComponentType): boolean;
    
    // Events
    on(event: EntityEventType, handler: EntityEventHandler): void;
    off(event: EntityEventType, handler: EntityEventHandler): void;
    emit(event: EntityEventType, data?: unknown): void;
}
